/**
 * WebSocket connection management
 */

import React, { createContext, useContext, useEffect, useRef, useCallback, useState } from "react";
import { decode } from "@msgpack/msgpack";
import { useViewerStore } from "./store";
import { dispatchInboundMessage, type MessageHandlerContext } from "./messageHandlers";
import type {
  Message,
  GuiEventMessage,
  TransformEventMessage,
  ClickEventMessage,
  AddModuleMessage,
  IKGizmoUpdateMessage,
  SetIKConstraintsMessage,
  EnableIKModeMessage,
  EnableSelfCollisionMessage,
  JointSliderUpdateMessage,
  ExportUrdfMessage,
  ExportUrdfResultMessage,
  ExportUrdfProgressMessage,
  GetUrdfForConfigMessage,
  UrdfForConfigResultMessage,
  UploadModuleMessage,
  UploadModuleResultMessage,
  SaveConfigResultMessage,
  EnterEditLinksMessage,
  ExitEditLinksMessage,
  SaveConnectableLinksMessage,
  SaveConnectableLinksResultMessage,
  AddEndCoordsMessage,
  UpdateEndCoordsMessage,
  DeleteEndCoordsMessage,
  HighlightInstanceMessage,
  ShowEndCoordsGizmoMessage,
  EndCoordsGizmoUpdateMessage,
  RenameJointMessage,
  RenameJointResultMessage,
  CreateJointGroupMessage,
  UpdateJointGroupMessage,
  DeleteJointGroupMessage,
  DetectKinematicChainsMessage,
  JointGroupResultMessage,
  UpdateJointSettingsMessage,
  JointSettingsResultMessage,
  HighlightJointMessage,
  GetChainJointsMessage,
  MeshFormat,
} from "./types";

interface WebSocketContextValue {
  isConnected: boolean;
  send: (message: Message) => void;
  sendGuiEvent: (uuid: string, eventType: "click" | "change" | "update", value: unknown) => void;
  sendTransformEvent: (
    uuid: string,
    position: [number, number, number],
    quaternion: [number, number, number, number]
  ) => void;
  sendClickEvent: (uuid: string, point: [number, number, number]) => void;
  sendAddModule: (moduleId: string, position?: [number, number, number], instanceId?: string) => void;
  sendPreviewConnection: (
    childInstanceId: string,
    childLinkName: string,
    parentInstanceId: string,
    parentLinkName: string,
    showPreview: boolean,
    yaw?: number,
    pitch?: number,
    roll?: number,
    offset?: [number, number, number]
  ) => void;
  sendConnectModules: (
    childInstanceId: string,
    childLinkName: string,
    parentInstanceId: string,
    parentLinkName: string,
    yaw?: number,
    pitch?: number,
    roll?: number,
    offset?: [number, number, number]
  ) => void;
  sendSelectInstance: (instanceId: string | null) => void;
  sendUpdateInstancePosition: (instanceId: string, position: [number, number, number]) => void;
  sendTranslationEnd: () => void;
  sendDeleteInstance: (instanceId: string) => void;
  sendDisconnectInstance: (instanceId: string) => void;
  sendClearAllInstances: () => void;
  sendSetInstancePrefix: (instanceId: string, prefix: string | null) => void;
  sendEnableIKMode: (enabled: boolean) => void;
  sendEnableSelfCollision: (enabled: boolean) => void;
  sendIKGizmoUpdate: (
    gizmoId: string,
    position: [number, number, number],
    quaternion: [number, number, number, number]
  ) => void;
  sendSetIKConstraints: (
    positionMask: [boolean, boolean, boolean],
    rotationMask: [boolean, boolean, boolean]
  ) => void;
  sendJointSliderUpdate: (jointName: string, value: number) => void;
  sendExportUrdf: (
    packageName: string,
    visualMeshFormat?: MeshFormat,
    collisionMeshFormat?: MeshFormat
  ) => void;
  sendSaveConfig: (configName: string) => void;
  sendLoadConfig: (configData: string) => void;
  sendEnterEditLinksMode: (instanceId: string) => void;
  sendExitEditLinksMode: () => void;
  sendSaveConnectableLinks: (
    moduleId: string,
    connectableLinks: string[],
    canBeChild: boolean,
    hasServo: boolean,
    endEffectorMode: "auto" | "none" | "custom",
    endEffectorLink: string | null,
    endEffectorOffsetXyz: [number, number, number],
    endEffectorOffsetRpy: [number, number, number]
  ) => void;
  sendAddEndCoords: (
    name: string,
    parentLinkName: string,
    position: [number, number, number],
    rotation: [number, number, number]
  ) => void;
  sendUpdateEndCoords: (
    name: string,
    parentLinkName: string,
    position: [number, number, number],
    rotation: [number, number, number]
  ) => void;
  sendDeleteEndCoords: (name: string) => void;
  sendHighlightInstance: (instanceId: string) => void;
  sendShowEndCoordsGizmo: (
    show: boolean,
    parentLinkName: string,
    position: [number, number, number],
    rotation: [number, number, number]
  ) => void;
  sendEndCoordsGizmoUpdate: (
    position: [number, number, number],
    quaternion: [number, number, number, number]
  ) => void;
  // Joint renaming
  sendRenameJoint: (jointName: string, newName: string) => void;
  // Joint groups
  sendCreateJointGroup: (name: string, jointNames: string[], color: string | null) => void;
  sendUpdateJointGroup: (name: string, newName: string | null, jointNames: string[], color: string | null) => void;
  sendDeleteJointGroup: (name: string) => void;
  sendDetectKinematicChains: () => void;
  // Joint settings
  sendUpdateJointSettings: (
    jointName: string,
    servoId: number | null,
    direction: 1 | -1,
    angleOffset: number,
    customMinAngle: number | null,
    customMaxAngle: number | null
  ) => void;
  // Mimic settings
  sendUpdateMimicSettings: (
    jointName: string,
    mimicJoint: string | null,
    mimicMultiplier: number,
    mimicOffset: number
  ) => void;
  // Joint highlight
  sendHighlightJoint: (jointName: string) => void;
  // Chain joints query (for MoveIt-style selection)
  sendGetChainJoints: (baseLink: string, tipLink: string) => void;
  // Get URDF for config generator
  sendGetUrdfForConfig: () => void;
  // Callback for urdf_for_config_result
  onUrdfForConfigResult: ((result: UrdfForConfigResultMessage) => void) | null;
  setOnUrdfForConfigResult: (callback: ((result: UrdfForConfigResultMessage) => void) | null) => void;
  // Upload custom module
  sendUploadModule: (name: string, dataBase64: string, fileType: "zip" | "urdf") => void;
  // Callback for upload_module_result
  onUploadModuleResult: ((result: UploadModuleResultMessage) => void) | null;
  setOnUploadModuleResult: (callback: ((result: UploadModuleResultMessage) => void) | null) => void;
  // Callback for export_urdf_result (used by Config Generator)
  onExportUrdfResult: ((result: ExportUrdfResultMessage) => void) | null;
  setOnExportUrdfResult: (callback: ((result: ExportUrdfResultMessage) => void) | null) => void;
  setOnExportUrdfProgress: (callback: ((progress: ExportUrdfProgressMessage) => void) | null) => void;
}

const WebSocketContext = createContext<WebSocketContextValue | null>(null);

export function useWebSocket(): WebSocketContextValue {
  const context = useContext(WebSocketContext);
  if (!context) {
    throw new Error("useWebSocket must be used within WebSocketProvider");
  }
  return context;
}

interface WebSocketProviderProps {
  url: string;
  children: React.ReactNode;
}

// Module-level tracking to prevent duplicate exports across multiple WebSocket providers
let globalExportInProgress: string | null = null;
// Track if a callback is expected for the current export (set when export is requested with callback)
let globalExportExpectsCallback = false;

// Reconnection parameters
const RECONNECT_BASE_DELAY_MS = 1000;
const RECONNECT_MAX_DELAY_MS = 30000;
const HEARTBEAT_INTERVAL_MS = 30000;

export function WebSocketProvider({ url, children }: WebSocketProviderProps) {
  const wsRef = useRef<WebSocket | null>(null);
  const reconnectTimeoutRef = useRef<number | null>(null);
  const reconnectAttemptRef = useRef(0);
  const heartbeatIntervalRef = useRef<number | null>(null);
  const urdfForConfigCallbackRef = useRef<((result: UrdfForConfigResultMessage) => void) | null>(null);
  const uploadModuleCallbackRef = useRef<((result: UploadModuleResultMessage) => void) | null>(null);
  const exportUrdfCallbackRef = useRef<((result: ExportUrdfResultMessage) => void) | null>(null);
  const exportUrdfProgressCallbackRef = useRef<((progress: ExportUrdfProgressMessage) => void) | null>(null);
  const [isConnected, setIsConnected] = useState(false);

  const store = useViewerStore;

  const connect = useCallback(() => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      return;
    }

    const ws = new WebSocket(url);
    wsRef.current = ws;

    ws.onopen = () => {
      console.log("WebSocket connected");
      setIsConnected(true);
      store.getState().setConnected(true);
      reconnectAttemptRef.current = 0;

      // Clear reconnect timeout
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = null;
      }

      // Start heartbeat ping
      if (heartbeatIntervalRef.current) {
        clearInterval(heartbeatIntervalRef.current);
      }
      heartbeatIntervalRef.current = window.setInterval(() => {
        if (ws.readyState === WebSocket.OPEN) {
          ws.send(JSON.stringify({ type: "ping" }));
        }
      }, HEARTBEAT_INTERVAL_MS);
    };

    ws.onclose = (event) => {
      console.log("WebSocket disconnected", event.code, event.reason);
      setIsConnected(false);
      store.getState().setConnected(false);

      // Stop heartbeat
      if (heartbeatIntervalRef.current) {
        clearInterval(heartbeatIntervalRef.current);
        heartbeatIntervalRef.current = null;
      }

      // Session belongs to another user — redirect to a new session
      if (event.code === 4403) {
        console.warn("Session access denied:", event.reason);
        window.location.href = "/";
        return;
      }

      // Exponential backoff with jitter: base * 2^attempt + random jitter
      const attempt = reconnectAttemptRef.current;
      const baseDelay = Math.min(
        RECONNECT_BASE_DELAY_MS * Math.pow(2, attempt),
        RECONNECT_MAX_DELAY_MS,
      );
      const jitter = Math.random() * RECONNECT_BASE_DELAY_MS;
      const delay = baseDelay + jitter;
      reconnectAttemptRef.current = attempt + 1;

      console.log(`Reconnecting in ${Math.round(delay)}ms (attempt ${attempt + 1})...`);
      reconnectTimeoutRef.current = window.setTimeout(() => {
        connect();
      }, delay);
    };

    ws.onerror = (error) => {
      console.error("WebSocket error:", error);
    };

    // Use arraybuffer for binary messages (MessagePack)
    ws.binaryType = "arraybuffer";

    ws.onmessage = (event) => {
      try {
        let message: Message;

        if (event.data instanceof ArrayBuffer) {
          // Binary message: decode with MessagePack
          message = decode(new Uint8Array(event.data)) as Message;
        } else {
          // Text message: parse as JSON
          message = JSON.parse(event.data) as Message;
        }

        handleMessage(message);
      } catch (error) {
        console.error("Failed to parse message:", error);
      }
    };
  }, [url]);

  const handleMessage = (message: Message) => {
    const context: MessageHandlerContext = {
      state: store.getState(),
      handleExportResult,
      handleExportProgress,
      handleSaveConfigResult,
      handleSaveConnectableLinksResult,
      handleRenameJointResult,
      handleJointGroupResult,
      handleJointSettingsResult,
      urdfForConfigCallbackRef,
      uploadModuleCallbackRef,
    };

    if (!dispatchInboundMessage(message, context)) {
      console.warn("Unknown message type:", message);
    }
  };

  const handleExportProgress = (message: ExportUrdfProgressMessage) => {
    exportUrdfProgressCallbackRef.current?.(message);
  };

  const handleExportResult = (message: ExportUrdfResultMessage) => {
    const filename = message.filename || "export";
    console.log("[WebSocket] handleExportResult called, callback:", !!exportUrdfCallbackRef.current, "globalExportInProgress:", globalExportInProgress, "expectsCallback:", globalExportExpectsCallback, "filename:", filename);

    // Check if this export is already being handled globally
    if (globalExportInProgress === filename) {
      console.log("[WebSocket] Export already in progress globally, skipping");
      return;
    }

    // If a callback is registered, use it instead of default behavior
    if (exportUrdfCallbackRef.current) {
      console.log("[WebSocket] Calling registered callback");
      globalExportInProgress = filename;
      globalExportExpectsCallback = false; // Reset the flag
      exportUrdfCallbackRef.current(message);
      // Clear after a delay to allow for async processing
      setTimeout(() => {
        if (globalExportInProgress === filename) {
          globalExportInProgress = null;
        }
      }, 5000);
      return;
    }

    // If another provider is expecting to handle this with a callback, skip default behavior
    if (globalExportExpectsCallback) {
      console.log("[WebSocket] Another provider expects callback, skipping default download");
      return;
    }

    // No callback expected, do default download
    console.log("[WebSocket] No callback, using default download behavior");
    if (message.success && message.data && message.filename) {
      globalExportInProgress = filename;

      // Decode base64 and trigger download
      const byteCharacters = atob(message.data);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: "application/zip" });

      // Create download link
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = message.filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      console.log(`Downloaded: ${message.filename}`);

      setTimeout(() => {
        if (globalExportInProgress === filename) {
          globalExportInProgress = null;
        }
      }, 1000);
    } else if (message.error) {
      console.error(`Export failed: ${message.error}`);
      alert(`Export failed: ${message.error}`);
    }
  };

  const handleSaveConfigResult = (message: SaveConfigResultMessage) => {
    if (message.success && message.data && message.filename) {
      // Create JSON blob and trigger download
      const blob = new Blob([message.data], { type: "application/json" });

      // Create download link
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = message.filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      console.log(`Downloaded: ${message.filename}`);
    } else if (message.error) {
      console.error(`Save config failed: ${message.error}`);
      alert(`Save config failed: ${message.error}`);
    }
  };

  const handleSaveConnectableLinksResult = (message: SaveConnectableLinksResultMessage) => {
    if (message.success) {
      console.log("Connectable links saved successfully");
    } else if (message.error) {
      console.error(`Save connectable links failed: ${message.error}`);
      alert(`Save connectable links failed: ${message.error}`);
    }
  };

  const handleRenameJointResult = (message: RenameJointResultMessage) => {
    if (message.success) {
      console.log(`Joint renamed: ${message.joint_name} -> ${message.new_name}`);
    } else if (message.error) {
      console.error(`Joint rename failed: ${message.error}`);
      alert(`Joint rename failed: ${message.error}`);
    }
  };

  const handleJointGroupResult = (message: JointGroupResultMessage) => {
    if (message.success) {
      console.log(`Joint group ${message.operation}: ${message.group_name}`);
    } else if (message.error) {
      console.error(`Joint group ${message.operation} failed: ${message.error}`);
      alert(`Joint group ${message.operation} failed: ${message.error}`);
    }
  };

  const handleJointSettingsResult = (message: JointSettingsResultMessage) => {
    if (message.success) {
      console.log(`Joint settings updated: ${message.joint_name}`);
    } else if (message.error) {
      console.error(`Joint settings update failed: ${message.error}`);
      alert(`Joint settings update failed: ${message.error}`);
    }
  };

  useEffect(() => {
    connect();

    return () => {
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
      if (heartbeatIntervalRef.current) {
        clearInterval(heartbeatIntervalRef.current);
      }
      if (wsRef.current) {
        wsRef.current.close();
      }
    };
  }, [connect]);

  const send = useCallback((message: Message) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      console.log("[WebSocket] Sending message:", message.type, message);
      wsRef.current.send(JSON.stringify(message));
    } else {
      console.error("[WebSocket] Cannot send - connection not open. State:", wsRef.current?.readyState, "Message:", message.type);
    }
  }, []);

  const sendGuiEvent = useCallback(
    (uuid: string, eventType: "click" | "change" | "update", value: unknown) => {
      const message: GuiEventMessage = {
        type: "gui_event",
        uuid,
        event_type: eventType,
        value,
      };
      send(message);
    },
    [send]
  );

  const sendTransformEvent = useCallback(
    (
      uuid: string,
      position: [number, number, number],
      quaternion: [number, number, number, number]
    ) => {
      const message: TransformEventMessage = {
        type: "transform_event",
        uuid,
        position,
        quaternion,
      };
      send(message);
    },
    [send]
  );

  const sendClickEvent = useCallback(
    (uuid: string, point: [number, number, number]) => {
      const message: ClickEventMessage = {
        type: "click_event",
        uuid,
        point,
      };
      send(message);
    },
    [send]
  );

  const sendAddModule = useCallback(
    (moduleId: string, position?: [number, number, number], instanceId?: string) => {
      const message: AddModuleMessage = {
        type: "add_module",
        module_id: moduleId,
        position: position ?? [0, 0, 0],
        prefix: null,
        instance_id: instanceId ?? null,
      };
      send(message);
    },
    [send]
  );

  const sendPreviewConnection = useCallback(
    (
      childInstanceId: string,
      childLinkName: string,
      parentInstanceId: string,
      parentLinkName: string,
      showPreview: boolean,
      yaw: number = 0,
      pitch: number = 0,
      roll: number = 0,
      offset: [number, number, number] = [0, 0, 0]
    ) => {
      const message = {
        type: "preview_connection" as const,
        child_instance_id: childInstanceId,
        child_link_name: childLinkName,
        parent_instance_id: parentInstanceId,
        parent_link_name: parentLinkName,
        show_preview: showPreview,
        yaw,
        pitch,
        roll,
        offset,
      };
      send(message as Message);
    },
    [send]
  );

  const sendConnectModules = useCallback(
    (
      childInstanceId: string,
      childLinkName: string,
      parentInstanceId: string,
      parentLinkName: string,
      yaw: number = 0,
      pitch: number = 0,
      roll: number = 0,
      offset: [number, number, number] = [0, 0, 0]
    ) => {
      const message = {
        type: "connect_modules" as const,
        child_instance_id: childInstanceId,
        child_link_name: childLinkName,
        parent_instance_id: parentInstanceId,
        parent_link_name: parentLinkName,
        yaw,
        pitch,
        roll,
        offset,
      };
      send(message as Message);
    },
    [send]
  );

  const sendSelectInstance = useCallback(
    (instanceId: string | null) => {
      const message = {
        type: "select_instance" as const,
        instance_id: instanceId ?? "",
      };
      send(message as Message);
    },
    [send]
  );

  const sendUpdateInstancePosition = useCallback(
    (instanceId: string, position: [number, number, number]) => {
      const message = {
        type: "update_instance_position" as const,
        instance_id: instanceId,
        position,
      };
      send(message as Message);
    },
    [send]
  );

  const sendTranslationEnd = useCallback(() => {
    const message = {
      type: "translation_end" as const,
    };
    send(message as Message);
  }, [send]);

  const sendDeleteInstance = useCallback(
    (instanceId: string) => {
      const message = {
        type: "delete_instance" as const,
        instance_id: instanceId,
      };
      send(message as Message);
    },
    [send]
  );

  const sendDisconnectInstance = useCallback(
    (instanceId: string) => {
      const message = {
        type: "disconnect_instance" as const,
        instance_id: instanceId,
      };
      send(message as Message);
    },
    [send]
  );

  const sendClearAllInstances = useCallback(() => {
    const message = {
      type: "clear_all_instances" as const,
    };
    send(message as Message);
  }, [send]);

  const sendSetInstancePrefix = useCallback(
    (instanceId: string, prefix: string | null) => {
      const message = {
        type: "set_instance_prefix" as const,
        instance_id: instanceId,
        prefix,
      };
      send(message as Message);
    },
    [send]
  );

  const sendEnableIKMode = useCallback(
    (enabled: boolean) => {
      const message: EnableIKModeMessage = {
        type: "enable_ik_mode",
        enabled,
      };
      send(message);
    },
    [send]
  );

  const sendEnableSelfCollision = useCallback(
    (enabled: boolean) => {
      const message: EnableSelfCollisionMessage = {
        type: "enable_self_collision",
        enabled,
      };
      send(message);
    },
    [send]
  );

  const sendIKGizmoUpdate = useCallback(
    (
      gizmoId: string,
      position: [number, number, number],
      quaternion: [number, number, number, number]
    ) => {
      const message: IKGizmoUpdateMessage = {
        type: "ik_gizmo_update",
        gizmo_id: gizmoId,
        position,
        quaternion,
      };
      send(message);
    },
    [send]
  );

  const sendSetIKConstraints = useCallback(
    (
      positionMask: [boolean, boolean, boolean],
      rotationMask: [boolean, boolean, boolean]
    ) => {
      const message: SetIKConstraintsMessage = {
        type: "set_ik_constraints",
        position_mask: positionMask,
        rotation_mask: rotationMask,
      };
      send(message);
    },
    [send]
  );

  const sendJointSliderUpdate = useCallback(
    (jointName: string, value: number) => {
      const message: JointSliderUpdateMessage = {
        type: "joint_slider_update",
        joint_name: jointName,
        value,
      };
      send(message);
    },
    [send]
  );

  const sendExportUrdf = useCallback(
    (
      packageName: string,
      visualMeshFormat: MeshFormat = "glb",
      collisionMeshFormat: MeshFormat = "glb"
    ) => {
      // If this provider has a callback, mark that we expect callback handling
      if (exportUrdfCallbackRef.current) {
        globalExportExpectsCallback = true;
        console.log("[WebSocket] sendExportUrdf - callback registered, setting globalExportExpectsCallback=true");
      }
      const message: ExportUrdfMessage = {
        type: "export_urdf",
        package_name: packageName,
        visual_mesh_format: visualMeshFormat,
        collision_mesh_format: collisionMeshFormat,
      };
      send(message);
    },
    [send]
  );

  const sendSaveConfig = useCallback(
    (configName: string) => {
      const message = {
        type: "save_config" as const,
        config_name: configName,
      };
      send(message as Message);
    },
    [send]
  );

  const sendLoadConfig = useCallback(
    (configData: string) => {
      const message = {
        type: "load_config" as const,
        config_data: configData,
      };
      send(message as Message);
    },
    [send]
  );

  const sendEnterEditLinksMode = useCallback(
    (instanceId: string) => {
      const message: EnterEditLinksMessage = {
        type: "enter_edit_links_mode",
        instance_id: instanceId,
      };
      send(message as Message);
    },
    [send]
  );

  const sendExitEditLinksMode = useCallback(() => {
    const message: ExitEditLinksMessage = {
      type: "exit_edit_links_mode",
    };
    send(message as Message);
  }, [send]);

  const sendSaveConnectableLinks = useCallback(
    (
      moduleId: string,
      connectableLinks: string[],
      canBeChild: boolean,
      hasServo: boolean,
      endEffectorMode: "auto" | "none" | "custom",
      endEffectorLink: string | null,
      endEffectorOffsetXyz: [number, number, number],
      endEffectorOffsetRpy: [number, number, number]
    ) => {
      const message: SaveConnectableLinksMessage = {
        type: "save_connectable_links",
        module_id: moduleId,
        connectable_links: connectableLinks,
        can_be_child: canBeChild,
        has_servo: hasServo,
        end_effector_mode: endEffectorMode,
        end_effector_link: endEffectorLink,
        end_effector_offset_xyz: endEffectorOffsetXyz,
        end_effector_offset_rpy: endEffectorOffsetRpy,
      };
      send(message as Message);
    },
    [send]
  );

  const sendAddEndCoords = useCallback(
    (
      name: string,
      parentLinkName: string,
      position: [number, number, number],
      rotation: [number, number, number]
    ) => {
      const message: AddEndCoordsMessage = {
        type: "add_end_coords",
        name,
        parent_link_name: parentLinkName,
        position,
        rotation,
      };
      send(message as Message);
    },
    [send]
  );

  const sendUpdateEndCoords = useCallback(
    (
      name: string,
      parentLinkName: string,
      position: [number, number, number],
      rotation: [number, number, number]
    ) => {
      const message: UpdateEndCoordsMessage = {
        type: "update_end_coords",
        name,
        parent_link_name: parentLinkName,
        position,
        rotation,
      };
      send(message as Message);
    },
    [send]
  );

  const sendDeleteEndCoords = useCallback(
    (name: string) => {
      const message: DeleteEndCoordsMessage = {
        type: "delete_end_coords",
        name,
      };
      send(message as Message);
    },
    [send]
  );

  const sendHighlightInstance = useCallback(
    (instanceId: string) => {
      const message: HighlightInstanceMessage = {
        type: "highlight_instance",
        instance_id: instanceId,
      };
      send(message as Message);
    },
    [send]
  );

  const sendShowEndCoordsGizmo = useCallback(
    (
      show: boolean,
      parentLinkName: string,
      position: [number, number, number],
      rotation: [number, number, number]
    ) => {
      const message: ShowEndCoordsGizmoMessage = {
        type: "show_end_coords_gizmo",
        show,
        parent_link_name: parentLinkName,
        position,
        rotation,
      };
      send(message as Message);
    },
    [send]
  );

  const sendEndCoordsGizmoUpdate = useCallback(
    (
      position: [number, number, number],
      quaternion: [number, number, number, number]
    ) => {
      const message: EndCoordsGizmoUpdateMessage = {
        type: "end_coords_gizmo_update",
        position,
        quaternion,
      };
      send(message as Message);
    },
    [send]
  );

  const sendRenameJoint = useCallback(
    (jointName: string, newName: string) => {
      const message: RenameJointMessage = {
        type: "rename_joint",
        joint_name: jointName,
        new_name: newName,
      };
      send(message);
    },
    [send]
  );

  const sendCreateJointGroup = useCallback(
    (name: string, jointNames: string[], color: string | null) => {
      const message: CreateJointGroupMessage = {
        type: "create_joint_group",
        name,
        joint_names: jointNames,
        color,
      };
      send(message as Message);
    },
    [send]
  );

  const sendUpdateJointGroup = useCallback(
    (name: string, newName: string | null, jointNames: string[], color: string | null) => {
      const message: UpdateJointGroupMessage = {
        type: "update_joint_group",
        name,
        new_name: newName,
        joint_names: jointNames,
        color,
      };
      send(message as Message);
    },
    [send]
  );

  const sendDeleteJointGroup = useCallback(
    (name: string) => {
      const message: DeleteJointGroupMessage = {
        type: "delete_joint_group",
        name,
      };
      send(message as Message);
    },
    [send]
  );

  const sendDetectKinematicChains = useCallback(() => {
    const message: DetectKinematicChainsMessage = {
      type: "detect_kinematic_chains",
    };
    send(message as Message);
  }, [send]);

  const sendUpdateJointSettings = useCallback(
    (
      jointName: string,
      servoId: number | null,
      direction: 1 | -1,
      angleOffset: number,
      customMinAngle: number | null,
      customMaxAngle: number | null
    ) => {
      const message: UpdateJointSettingsMessage = {
        type: "update_joint_settings",
        joint_name: jointName,
        servo_id: servoId,
        direction,
        angle_offset: angleOffset,
        custom_min_angle: customMinAngle,
        custom_max_angle: customMaxAngle,
      };
      send(message as Message);
    },
    [send]
  );

  const sendUpdateMimicSettings = useCallback(
    (
      jointName: string,
      mimicJoint: string | null,
      mimicMultiplier: number,
      mimicOffset: number
    ) => {
      const message = {
        type: "update_mimic_settings" as const,
        joint_name: jointName,
        mimic_joint: mimicJoint,
        mimic_multiplier: mimicMultiplier,
        mimic_offset: mimicOffset,
      };
      send(message as Message);
    },
    [send]
  );

  const sendHighlightJoint = useCallback(
    (jointName: string) => {
      const message: HighlightJointMessage = {
        type: "highlight_joint",
        joint_name: jointName,
      };
      send(message as Message);
    },
    [send]
  );

  const sendGetChainJoints = useCallback(
    (baseLink: string, tipLink: string) => {
      const message: GetChainJointsMessage = {
        type: "get_chain_joints",
        base_link: baseLink,
        tip_link: tipLink,
      };
      send(message as Message);
    },
    [send]
  );

  const sendGetUrdfForConfig = useCallback(() => {
    const message: GetUrdfForConfigMessage = {
      type: "get_urdf_for_config",
    };
    send(message as Message);
  }, [send]);

  const setOnUrdfForConfigResult = useCallback(
    (callback: ((result: UrdfForConfigResultMessage) => void) | null) => {
      urdfForConfigCallbackRef.current = callback;
    },
    []
  );

  const setOnExportUrdfResult = useCallback(
    (callback: ((result: ExportUrdfResultMessage) => void) | null) => {
      console.log("[WebSocket] setOnExportUrdfResult called, callback:", callback ? "function" : "null");
      exportUrdfCallbackRef.current = callback;
    },
    []
  );

  const setOnExportUrdfProgress = useCallback(
    (callback: ((progress: ExportUrdfProgressMessage) => void) | null) => {
      exportUrdfProgressCallbackRef.current = callback;
    },
    []
  );

  const sendUploadModule = useCallback(
    (name: string, dataBase64: string, fileType: "zip" | "urdf") => {
      const message: UploadModuleMessage = {
        type: "upload_module",
        name,
        data_base64: dataBase64,
        file_type: fileType,
      };
      send(message as Message);
    },
    [send]
  );

  const setOnUploadModuleResult = useCallback(
    (callback: ((result: UploadModuleResultMessage) => void) | null) => {
      uploadModuleCallbackRef.current = callback;
    },
    []
  );

  const contextValue: WebSocketContextValue = {
    isConnected,
    send,
    sendGuiEvent,
    sendTransformEvent,
    sendClickEvent,
    sendAddModule,
    sendPreviewConnection,
    sendConnectModules,
    sendSelectInstance,
    sendUpdateInstancePosition,
    sendTranslationEnd,
    sendDeleteInstance,
    sendDisconnectInstance,
    sendClearAllInstances,
    sendSetInstancePrefix,
    sendEnableIKMode,
    sendEnableSelfCollision,
    sendIKGizmoUpdate,
    sendSetIKConstraints,
    sendJointSliderUpdate,
    sendExportUrdf,
    sendSaveConfig,
    sendLoadConfig,
    sendEnterEditLinksMode,
    sendExitEditLinksMode,
    sendSaveConnectableLinks,
    sendAddEndCoords,
    sendUpdateEndCoords,
    sendDeleteEndCoords,
    sendHighlightInstance,
    sendShowEndCoordsGizmo,
    sendEndCoordsGizmoUpdate,
    sendRenameJoint,
    sendCreateJointGroup,
    sendUpdateJointGroup,
    sendDeleteJointGroup,
    sendDetectKinematicChains,
    sendUpdateJointSettings,
    sendUpdateMimicSettings,
    sendHighlightJoint,
    sendGetChainJoints,
    sendGetUrdfForConfig,
    onUrdfForConfigResult: urdfForConfigCallbackRef.current,
    setOnUrdfForConfigResult,
    sendUploadModule,
    onUploadModuleResult: uploadModuleCallbackRef.current,
    setOnUploadModuleResult,
    onExportUrdfResult: exportUrdfCallbackRef.current,
    setOnExportUrdfResult,
    setOnExportUrdfProgress,
  };

  return (
    <WebSocketContext.Provider value={contextValue}>{children}</WebSocketContext.Provider>
  );
}
